import {
	CheckIcon,
	ChevronDownIcon,
	CircleAlertIcon,
	LoaderIcon,
	WrenchIcon,
} from "lucide-react";
import type { ComponentProps } from "react";
import { CodeBlock } from "@/components/ai-elements/code-block";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

// Visual style follows https://elements.ai-sdk.dev/components/tool
// (bordered card, icon+state header, JSON blocks) — replaces old KeyValueList render.

export type ToolCallState = "running" | "done" | "error";

export const Tool = ({
	className,
	...props
}: ComponentProps<typeof Collapsible>) => (
	<Collapsible
		className={cn(
			"not-prose group/tool w-full overflow-hidden rounded-lg border bg-card/40",
			className,
		)}
		{...props}
	/>
);

const STATE_LABEL: Record<ToolCallState, string> = {
	running: "Running",
	done: "Completed",
	error: "Failed",
};

export const ToolHeader = ({
	className,
	title,
	state,
	...props
}: {
	title: string;
	state: ToolCallState;
	className?: string;
}) => (
	<CollapsibleTrigger
		className={cn(
			"flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/40",
			className,
		)}
		{...props}
	>
		<WrenchIcon className="size-3.5 shrink-0 text-muted-foreground" />
		<span className="flex-1 truncate font-mono text-xs font-medium">
			{title}
		</span>
		<span
			className={cn(
				"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
				state === "running" && "bg-primary/10 text-primary",
				state === "done" && "bg-success/10 text-success",
				state === "error" && "bg-destructive/10 text-destructive",
			)}
		>
			{state === "running" ? (
				<LoaderIcon className="size-2.5 animate-spin" />
			) : state === "error" ? (
				<CircleAlertIcon className="size-2.5" />
			) : (
				<CheckIcon className="size-2.5" />
			)}
			{STATE_LABEL[state]}
		</span>
		<ChevronDownIcon className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/tool:rotate-180" />
	</CollapsibleTrigger>
);

export const ToolContent = ({
	className,
	...props
}: ComponentProps<typeof CollapsibleContent>) => (
	<CollapsibleContent
		className={cn("border-t bg-background/50", className)}
		{...props}
	/>
);

function safeStringify(value: unknown): string {
	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return String(value);
	}
}

export const ToolInput = ({
	className,
	input,
	...props
}: ComponentProps<"div"> & { input?: unknown }) => {
	if (
		input === undefined ||
		input === null ||
		(typeof input === "object" &&
			!Array.isArray(input) &&
			Object.keys(input as Record<string, unknown>).length === 0)
	) {
		return null;
	}
	return (
		<div className={cn("space-y-1.5 px-3 py-2", className)} {...props}>
			<div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
				Input
			</div>
			<CodeBlock code={safeStringify(input)} language="json" />
		</div>
	);
};

export const ToolOutput = ({
	className,
	output,
	errorText,
	...props
}: ComponentProps<"div"> & {
	output?: unknown;
	errorText?: string;
}) => {
	if (output === undefined && !errorText) return null;
	return (
		<div
			className={cn("space-y-1.5 border-t px-3 py-2", className)}
			{...props}
		>
			<div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
				{errorText ? "Error" : "Output"}
			</div>
			{errorText ? (
				<p className="text-xs text-destructive">{errorText}</p>
			) : typeof output === "string" ? (
				<p className="whitespace-pre-wrap break-words text-xs">{output}</p>
			) : (
				<CodeBlock code={safeStringify(output)} language="json" />
			)}
		</div>
	);
};
